import React, { useEffect, useRef, useState } from "react";
import type { Editor as TipTapEditor } from "@tiptap/core";
import { Menu } from "primereact/menu";
import { Tooltip } from "primereact/tooltip";
import { OverlayPanel } from "primereact/overlaypanel";
import { ColorPicker } from "primereact/colorpicker";
import { ExplainedButton as Button, explainMenuItem } from '../explained-button';

import {
  IconBold,
  IconItalic,
  IconUnderline,
  IconStrikethrough,
  IconUndo,
  IconRedo,
  IconFontSizeDown,
  IconFontSizeUp,
} from "./editor-icons";

import { useManagedModuleEditor } from "../../providers/managed-module-management-provider";
import "../../assets/ContentEditor.css";
import { canInsertCallout, getAlignModel, getFontsModel, getHeadingModel, getInsertModel, getTableModel, getTooltipOptions, insertCollapsibleSection } from "./rich-toolbar-menus";
import { PanelPropertiesDialog } from "./panel-properties-dialog";
import { FormFieldDto, MessageDto } from "../../general/dtos";
import { TieredMenu } from "primereact/tieredmenu";
import { fileToBase64 } from "../../general/utilities";
import { useApi } from "../../providers/api-provider";
import { GeneralErrorDialog } from "../dialogs/error-dialog";
import { TablePropertiesDialog } from "./table-dialog";
import { StyleInspectorModal } from "../dialogs/style-inspector-dialog";
import { GraphDialog } from "../content-creator/graph-dialog";
import { GraphMetadata } from "./tiptap-graph-node-view";
import { LinkDialog } from "./link-dialog";
import { ExternalEmbedDialog } from "./external-embed-dialog";
import { SpecialCharacterDialog } from './special-character-dialog';
import { PasteMenu } from "./paste-menu";
import { FieldContainerDialog } from './field-container-dialog';
import { insideFieldContainer, insertFormField } from './field-container';
import { precedingFragments } from '../content-viewer/fragment-answers';
import { CardGroupDialog } from './card-group-dialog';
import { addCard, cardContext, changeCard } from './card-group';
import { changeColumn, columnsContext, setColumns } from './columns';

interface RichToolbarProps {
  editor: TipTapEditor | null;
}

export const RichToolbar: React.FC<RichToolbarProps> = ({ editor }) => {
  const api = useApi();
  const { registerFieldCreatedHandler, fragments, selectedFragmentKey } = useManagedModuleEditor();
  const headingMenu = useRef<TieredMenu | null>(null);
  const fontMenu = useRef<Menu | null>(null);
  const panelMenu = useRef<Menu | null>(null);
  const alignMenu = useRef<Menu | null>(null);
  const insertMenu = useRef<TieredMenu | null>(null);
  const tableMenu = useRef<Menu | null>(null);
  const blocksMenu = useRef<TieredMenu | null>(null);

  const colorOp = useRef<OverlayPanel | null>(null);
  const bgColorOp = useRef<OverlayPanel | null>(null);

  useEffect(() => {
    if (!editor) return;
    const dismissMenus = (event: Event) => {
      const menuEvent = event as unknown as React.SyntheticEvent;
      for (const ref of [fontMenu, panelMenu, alignMenu, tableMenu]) {
        ref.current?.hide(menuEvent);
      }
      // PrimeReact exposes hide at runtime but omits it from TieredMenu's typings.
      for (const ref of [headingMenu, insertMenu, blocksMenu]) {
        const menu = ref.current as (TieredMenu & { hide(event: React.SyntheticEvent): void }) | null;
        // An unmounted popup has no internal menu ref; hide() dereferences it.
        if (menu?.getElement()) menu.hide(menuEvent);
      }
      colorOp.current?.hide();
      bgColorOp.current?.hide();
    };
    const dom = editor.view.dom;
    dom.addEventListener('pointerdown', dismissMenus, true);
    dom.addEventListener('click', dismissMenus, true);
    return () => {
      dom.removeEventListener('pointerdown', dismissMenus, true);
      dom.removeEventListener('click', dismissMenus, true);
    };
  }, [editor]);

  const [colorValue, setColorValue] = useState<string>("#000000");
  const [bgColorValue, setBgColorValue] = useState<string>("#ffff00");

  const savedSelectionRef = useRef<{ from: number; to: number } | null>(null);

  const { openNewFieldEditor, toggleFieldsPanel } = useManagedModuleEditor();

  const [panelDialogVisible, setPanelDialogVisible] = useState(false);
  const [cardGroupRequest, setCardGroupRequest] = useState(0);
  const [, setBlockSelection] = useState('');
  const [isEditorPanelActive, setIsEditorPanelActive] = useState(false);
  const [isCollapsibleActive, setIsCollapsibleActive] = useState(false);

  const [errorTitle, setErrorTitle] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<string>('');

  const [tablePropsVisible, setTablePropsVisible] = useState(false);
  const [isTableActive, setIsTableActive] = useState(false);
  const [isTextSelected, setIsTextSelected] = useState(false);
  const [calloutInsertionAllowed, setCalloutInsertionAllowed] = useState(false);
  const [inFieldContainer, setInFieldContainer] = useState(false);
  const [fieldContainerRequest, setFieldContainerRequest] = useState<{ editor: TipTapEditor; id: number } | null>(null);

  const [isInspectorOpen, setIsInspectorOpen] = useState(false);

  const [graphDialogVisible, setGraphDialogVisible] = useState<boolean>(false);
  const [editingGraphData, setEditingGraphData] = useState<GraphMetadata | null>(null);
  const [linkDialogRequest, setLinkDialogRequest] = useState<{ editor: TipTapEditor; id: number } | null>(null);
  const [embedDialogRequest, setEmbedDialogRequest] = useState<{ editor: TipTapEditor; id: number } | null>(null);
  const [characterDialogRequest, setCharacterDialogRequest] = useState<{ editor: TipTapEditor; id: number } | null>(null);

  const isInsideTable = () => {
    if (editor) {
        const { $from } = editor.state.selection;
        // We look from the current depth up to the root (0)
        for (let d = $from.depth; d > 0; d--) {
            if ($from.node(d).type.name === 'table') {
                return true;
            }
        }
    }
    return false;
  };

  useEffect(() => {
    if (!editor) return;

    if (editor) {
        registerFieldCreatedHandler((field: FormFieldDto) => {
            if (field.type !== 'quiz-list') {
                insertFormField(editor, field);
            }
        });
    }

    const update = () => {
        setIsEditorPanelActive(editor.isActive("editorPanel"));
        setIsCollapsibleActive(editor.isActive("collapsible"));
        setIsTableActive(editor.isActive("table") || isInsideTable());
        const selection = editor.state?.selection;
        setIsTextSelected(selection ? !selection.empty : false);
        setCalloutInsertionAllowed(canInsertCallout(editor));
        setInFieldContainer(insideFieldContainer(editor));
        const cards = cardContext(editor);
        const columns = columnsContext(editor);
        setBlockSelection(JSON.stringify([
            cards && [cards.pos, cards.index, cards.group.childCount],
            columns && [columns.pos, columns.index, columns.group.childCount, editor.isActive('layoutColumn')],
            ...['bulletList', 'orderedList', 'blockquote', 'codeBlock'].map(name => editor.isActive(name))
        ]));
    };

    update();

    editor.on("selectionUpdate", update);
    editor.on("transaction", update);

    // The Toolbar "plugs in" its local logic to the editor extension.
    // When the NodeView calls the command, it triggers this callback.
    const extensionStorage = editor.storage.graphComponent;
      if (extensionStorage) {
        extensionStorage.onEdit = (attrs: GraphMetadata) => {
            handleEditExistingGraph(attrs);
        };
    }

    return () => {
        editor.off("selectionUpdate", update);
        editor.off("transaction", update);
    };

  }, [editor]);

  if (!editor) return null;

  const changeFontSize = (delta: number) => {
    const sizeClasses = [
        50, 55, 60, 65, 70, 75, 80, 85, 90, 95,
        100, 110, 120, 130, 140, 150, 160, 170,
        180, 190, 200
    ];

    const { state } = editor;
    const { from, to } = state.selection;

    const tr = state.tr;

    // Step 1: collect all slices that need updating
    const slices: Array<{ from: number; to: number; newSize: number }> = [];

    state.doc.nodesBetween(from, to, (node, pos) => {
        if (!node.isText) return;

        const nodeStart = pos;
        const nodeEnd = pos + node.nodeSize;

        const sliceFrom = Math.max(from, nodeStart);
        const sliceTo = Math.min(to, nodeEnd);

        if (sliceFrom >= sliceTo) return;

        const mark = node.marks.find(m => m.type.name === 'fontSize');
        let currentSize = 100;

        if (mark) {
            const match = mark.attrs.class.match(/text-(\d+)/);
            if (match) currentSize = parseInt(match[1], 10);
        }

        let index = sizeClasses.indexOf(currentSize);
        if (index === -1) index = sizeClasses.indexOf(100);

        let newIndex = index + delta;
        newIndex = Math.max(0, Math.min(sizeClasses.length - 1, newIndex));

        const newSize = sizeClasses[newIndex];

        slices.push({ from: sliceFrom, to: sliceTo, newSize });
    });

    // Step 2: apply all marks in a single transaction
    for (const slice of slices) {
        tr.addMark(
            slice.from,
            slice.to,
            editor.schema.marks.fontSize.create({ class: `text-${slice.newSize}` })
        );
    }

    // Step 3: dispatch once
    editor.view.dispatch(tr);

    // Step 4: restore selection
    editor.commands.setTextSelection({ from, to });
    editor.commands.focus();
};

  const applyColor = (hex: string) => {
    const sel = savedSelectionRef.current;
    if (!sel) return;
    const normalized = hex.startsWith("#") ? hex : `#${hex}`;
    editor.chain().focus().setTextSelection(sel).setColor(normalized).run();
    savedSelectionRef.current = null;
    colorOp.current?.hide();
  };

  const clearColor = () => {
    const sel = savedSelectionRef.current;
    if (!sel) return;
    editor.chain().focus().setTextSelection(sel).unsetColor().run();
    savedSelectionRef.current = null;
    colorOp.current?.hide();
  };

  const applyBgColor = (hex: string) => {
    const sel = savedSelectionRef.current;
    if (!sel) return;
    editor.chain().focus().setTextSelection(sel).setBgColor(hex).run();
    savedSelectionRef.current = null;
    bgColorOp.current?.hide();
  };

  const clearBgColor = () => {
    const sel = savedSelectionRef.current;
    if (!sel) return;
    editor.chain().focus().setTextSelection(sel).unsetBgColor().run();
    savedSelectionRef.current = null;
    bgColorOp.current?.hide();
  };

  async function handleInsertImageFromFile(editor: TipTapEditor) {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";

    input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
        alert("Image too large (max 2MB)");
        return;
        }

        const base64 = await fileToBase64(file);

        editor
        .chain()
        .focus()
        .setImage({ src: base64 })
        .run();
    };

    input.click();
  }

  async function handleInsertImageFromUrl(editor: TipTapEditor) {
    const url = window.prompt("Enter image URL:")?.trim();
    if (!url) return;

    try {
        if (!api) throw new Error("The API is not ready. Please try again.");
        const base64 = await api.importImage(url);
        if (editor.isDestroyed) return;

        editor
        .chain()
        .focus()
        .setImage({ src: base64 })
        .run();
    } catch (err) {
        const failure = err as { response?: { data?: { message?: unknown } }; message?: unknown } | null;
        const detail = failure?.response?.data?.message || failure?.message;
        setErrorTitle("Image import failed");
        setError("The image could not be imported.");
        setErrorDetails(typeof detail === "string" ? detail : "Failed to load image.");
    }
  }

  function extractYouTubeId(url: string): string | null {
    try {
        const u = new URL(url);

        if (u.hostname.includes("youtube.com")) {
        return u.searchParams.get("v");
        }

        if (u.hostname.includes("youtu.be")) {
        return u.pathname.slice(1);
        }

        return null;
    } catch {
        return null;
    }
  }

  function handleInsertYouTube(editor: TipTapEditor) {
    const url = window.prompt("Enter YouTube URL:");
    if (!url) return;

    const id = extractYouTubeId(url);
    if (!id) {
        alert("Invalid YouTube URL");
        return;
    }

    editor.chain().focus().setVideo({
        url,
        provider: "youtube",
        videoId: id,
    }).run();
  }

  function pickAudioFile(): Promise<File> {
    return new Promise(resolve => {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "audio/*";

            input.onchange = () => {
            const file = input.files?.[0];
            if (file) resolve(file);
        };
        input.click();
    });
  }

  async function handleInsertAudio(editor: TipTapEditor) {
    const file = await pickAudioFile();
    if (file) {
        try {
            if (!api) throw new Error("The API is not ready. Please try again.");
            const response: MessageDto = await api.uploadAudio(file);
            const url: string = response.message;
            if (url.startsWith("http://") || url.startsWith("https://")) {
                editor.chain().focus().setAudio({ src: url, align: "center" }).run();
            } else {
                throw new Error("Invalid URL returned from server");
            }
        } catch (err) {
            setErrorTitle("Audio upload failed");
            setError("An error occurred while uploading the audio file. Please try again.");
            setErrorDetails(err instanceof Error ? err.message : String(err));
        }
    }
  }

  // Logic to open for a NEW graph
  const handleOpenNewGraph = () => {
    setEditingGraphData(null); // Ensure we aren't carrying over old data
    setGraphDialogVisible(true);
  };

  // Logic to open for an EXISTING graph (Call this when the meta tag is clicked)
  const handleEditExistingGraph = (attrs: GraphMetadata) => {
    setEditingGraphData(attrs);
    setGraphDialogVisible(true);
  };

  const handleSaveGraph = (data: GraphMetadata) => {
    if (!editor || editor.isDestroyed) throw new Error('The editor is no longer available. Reopen the fragment before saving this graph.');
    if (data.height != null && !editor.schema.nodes.graphComponent?.spec.attrs?.height) {
      throw new Error('This editor was opened before graph sizing was added. Save your module, then reload this page and set the graph height again.');
    }
    if (editingGraphData) {
      editor?.chain().focus().updateAttributes('graphComponent', data).run();
    } else {
      editor?.chain().focus().insertContent({ type: 'graphComponent', attrs: data }).run();
    }
    setGraphDialogVisible(false);
  };

  const fontModel = [
    ...getFontsModel(editor),
    // {
    //   label: "Text Color…",
    //   command: (event: any) => {
    //     const sel = editor.state.selection;
    //     savedSelectionRef.current = { from: sel.from, to: sel.to };
    //     const attrs = editor.getAttributes("textStyle");
    //     setColorValue(attrs?.color ?? "#000000");
    //     colorOp.current?.toggle(event.originalEvent || event);
    //   },
    // },
    // {
    //   label: "Background Color…",
    //   command: (event: any) => {
    //     const sel = editor.state.selection;
    //     savedSelectionRef.current = { from: sel.from, to: sel.to };
    //     const attrs = editor.getAttributes("textStyle");
    //     setBgColorValue(attrs?.backgroundColor ?? "#ffff00");
    //     bgColorOp.current?.toggle(event.originalEvent || event);
    //   },
    // },
    // { label: "Remove Color", command: () => editor.chain().focus().unsetColor().run() },
    // { label: "Remove Background", command: () => editor.chain().focus().unsetBackgroundColor().run() },
  ];

  const cards = cardContext(editor);
  const inCard = editor.isActive('editorCard');
  const columns = columnsContext(editor);
  const inColumn = editor.isActive('layoutColumn');
  const blocksModel = [
    { label: 'Bullet list', icon: editor.isActive('bulletList') ? 'pi pi-check' : 'pi pi-list', command: () => editor.chain().focus().toggleBulletList().run() },
    { label: 'Numbered list', icon: editor.isActive('orderedList') ? 'pi pi-check' : 'pi pi-sort-numeric-down', command: () => editor.chain().focus().toggleOrderedList().run() },
    { label: 'Blockquote', icon: editor.isActive('blockquote') ? 'pi pi-check' : 'pi pi-comment', command: () => editor.chain().focus().toggleBlockquote().run() },
    { label: 'Code block', icon: editor.isActive('codeBlock') ? 'pi pi-check' : 'pi pi-code', command: () => editor.chain().focus().toggleCodeBlock().run() },
    { separator: true },
    { label: 'Columns', icon: 'pi pi-th-large', items: [
      ...([2, 3] as const).map(count => ({ label: `${count} columns`, icon: columns?.group.childCount === count ? 'pi pi-check' : undefined, command: () => setColumns(editor, count) })),
      { separator: true },
      { label: 'Move column earlier', icon: 'pi pi-arrow-left', disabled: !inColumn || !columns || columns.index === 0, command: () => changeColumn(editor, 'previous') },
      { label: 'Move column later', icon: 'pi pi-arrow-right', disabled: !inColumn || !columns || columns.index >= columns.group.childCount - 1, command: () => changeColumn(editor, 'next') },
      { label: 'Remove columns (keep content)', disabled: !columns, command: () => changeColumn(editor, 'unwrap') }
    ] }
  ];
  const panelModel = [
    { label: "Insert panel", command: () => editor.chain().focus().wrapInEditorPanel().run() },
    { label: "Remove panel", command: () => editor.chain().focus().unwrapEditorPanel().run(), disabled: !editor.isActive("editorPanel"), disabledReason: 'Click inside a panel first.' },
    { separator: true },
    { label: "Panel properties…", command: () => setPanelDialogVisible(true), disabled: !isEditorPanelActive, disabledReason: 'Click inside a panel to edit its properties.' },
    { separator: true },
    { label: "Collapsible panel", command: () => insertCollapsibleSection(editor) },
    { separator: true },
    { label: "Collapsible properties…", command: () => setPanelDialogVisible(true), disabled: !isCollapsibleActive, disabledReason: 'Click inside a collapsible panel to edit its properties.' },
    { separator: true },
    { label: 'Insert card group…', icon: 'pi pi-th-large', command: () => setCardGroupRequest(value => value + 1), disabled: !!cards, disabledReason: 'Click outside the current card group before inserting another group.' },
    { label: 'Card group properties…', command: () => setCardGroupRequest(value => value + 1), disabled: !cards, disabledReason: 'Click inside a card group first.' },
    { label: 'Add card', icon: 'pi pi-plus', command: () => addCard(editor), disabled: !cards, disabledReason: 'Click inside a card group first.' },
    { label: 'Remove card', icon: 'pi pi-minus', command: () => changeCard(editor, 'remove'), disabled: !inCard || !cards || cards.group.childCount <= 1, disabledReason: !inCard || !cards ? 'Click inside the card you want to remove.' : 'A card group must contain at least one card.' },
    { label: 'Move card earlier', icon: 'pi pi-arrow-left', command: () => changeCard(editor, 'previous'), disabled: !inCard || !cards || cards.index === 0, disabledReason: !inCard || !cards ? 'Click inside a card first.' : 'This is already the first card.' },
    { label: 'Move card later', icon: 'pi pi-arrow-right', command: () => changeCard(editor, 'next'), disabled: !inCard || !cards || cards.index >= cards.group.childCount - 1, disabledReason: !inCard || !cards ? 'Click inside a card first.' : 'This is already the last card.' },
    { label: 'Ungroup cards', command: () => changeCard(editor, 'ungroup'), disabled: !cards, disabledReason: 'Click inside a card group first.' },
  ].map(item => explainMenuItem(item, item.disabledReason));

  return (
    <div className="ce-toolbar">
      <Tooltip target=".ce-btn, .ce-btn-text" {...getTooltipOptions()} />

      <div className="ce-row">
        <PasteMenu editor={editor} disabled={inFieldContainer} onError={message => {
            setErrorTitle('Paste failed'); setError('The clipboard content could not be pasted.'); setErrorDetails(message);
        }} />
        <Button disabled={inFieldContainer} disabledReason="Click outside the form field to edit the surrounding lesson content." className="ce-btn" icon="pi pi-pencil" text onClick={(e) => headingMenu.current?.toggle(e)}
          aria-label="Text styles" title="Text styles" data-pr-tooltip="Text styles" />
        <TieredMenu popup ref={headingMenu} model={getHeadingModel(editor)} />

        <Button disabled={inFieldContainer} disabledReason="Click outside the form field to edit the surrounding lesson content." className="ce-btn" icon="pi pi-language" text onClick={(e) => fontMenu.current?.toggle(e)}
          aria-label="Font family and color" title="Font family and color" data-pr-tooltip="Font family and color" />
        <Menu popup ref={fontMenu} model={fontModel} />

        <Button disabled={inFieldContainer} disabledReason="Click outside the form field to edit the surrounding lesson content." className="ce-btn" text onClick={() => changeFontSize(-1)}
          aria-label="Decrease font size" title="Decrease font size" data-pr-tooltip="Decrease font size">
          <IconFontSizeDown />
        </Button>
        <Button disabled={inFieldContainer} disabledReason="Click outside the form field to edit the surrounding lesson content." className="ce-btn" text onClick={() => changeFontSize(1)}
          aria-label="Increase font size" title="Increase font size" data-pr-tooltip="Increase font size">
          <IconFontSizeUp />
        </Button>

        <Button disabled={inFieldContainer} disabledReason="Click outside the form field to edit the surrounding lesson content." className="ce-btn" text onClick={() => editor.chain().focus().toggleBold().run()} aria-label="Bold" title="Bold" data-pr-tooltip="Bold">
          <IconBold />
        </Button>
        <Button disabled={inFieldContainer} disabledReason="Click outside the form field to edit the surrounding lesson content." className="ce-btn" text onClick={() => editor.chain().focus().toggleItalic().run()} aria-label="Italic" title="Italic" data-pr-tooltip="Italic">
          <IconItalic />
        </Button>
        <Button disabled={inFieldContainer} disabledReason="Click outside the form field to edit the surrounding lesson content." className="ce-btn" text onClick={() => editor.chain().focus().toggleUnderline().run()} aria-label="Underline" title="Underline" data-pr-tooltip="Underline">
          <IconUnderline />
        </Button>
        <Button disabled={inFieldContainer} disabledReason="Click outside the form field to edit the surrounding lesson content." className="ce-btn" text onClick={() => editor.chain().focus().toggleStrike().run()} aria-label="Strikethrough" title="Strikethrough" data-pr-tooltip="Strikethrough">
          <IconStrikethrough />
        </Button>

        <Button disabled={inFieldContainer} disabledReason="Click outside the form field to edit the surrounding lesson content." className="ce-btn" icon="pi pi-window-maximize" text onClick={(e) => panelMenu.current?.toggle(e)}
          aria-label="Panel" title="Panel" data-pr-tooltip="Panel" />
        <Menu popup ref={panelMenu} model={panelModel} />

        <Button disabled={inFieldContainer} disabledReason="Click outside the form field to edit the surrounding lesson content." className="ce-btn" icon="pi pi-align-left" text onClick={(e) => alignMenu.current?.toggle(e)}
          aria-label="Align" title="Align" data-pr-tooltip="Align" />
        <Menu popup ref={alignMenu} model={getAlignModel(editor)} />

        <Button disabled={inFieldContainer} disabledReason="Click outside the form field to edit the surrounding lesson content." className="ce-btn" icon="pi pi-objects-column" text onClick={event => blocksMenu.current?.toggle(event)}
          aria-label="Blocks" title="Blocks" data-pr-tooltip="Blocks" aria-haspopup="menu" />
        <TieredMenu popup ref={blocksMenu} model={blocksModel} />

        <Button className="ce-btn" text onClick={() => editor.chain().focus().undo().run()} aria-label="Undo" title="Undo" data-pr-tooltip="Undo">
          <IconUndo />
        </Button>
        <Button className="ce-btn" text onClick={() => editor.chain().focus().redo().run()} aria-label="Redo" title="Redo" data-pr-tooltip="Redo">
          <IconRedo />
        </Button>

        {!isTextSelected && (
            <Button disabled={inFieldContainer} disabledReason="Click outside the form field to edit the surrounding lesson content." className="ce-btn" icon="pi pi-code" tooltip="Inspect styles" onClick={() => setIsInspectorOpen(true)} />
        )}

        <Button className="ce-btn" icon="pi pi-plus" text onClick={(e) => insertMenu.current?.toggle(e)}
          aria-label="Insert" title="Insert" data-pr-tooltip="Insert" />
        <TieredMenu popup ref={insertMenu} model={[
            { label: "Link...", icon: "pi pi-link", command: () => setLinkDialogRequest(request => ({ editor, id: (request?.id ?? 0) + 1 })) },
            { label: "Insert field", icon: "pi pi-plus", command: () => openNewFieldEditor() },
            { label: 'Field container…', icon: 'pi pi-table', disabled: inFieldContainer,
              command: () => setFieldContainerRequest(value => ({ editor, id: (value?.id ?? 0) + 1 })) },
            { label: "Insert answers table", icon: "pi pi-table",
              disabled: precedingFragments(fragments, selectedFragmentKey).length === 0,
              command: () => editor.chain().focus().insertContent({ type: 'fragmentAnswers',
                attrs: { 'fragment-key': precedingFragments(fragments, selectedFragmentKey)[0]?.key } }).run() },
            { label: "Manage fields", icon: "pi pi-list", command: () => toggleFieldsPanel() },
            { separator: true },
            { label: "Upload image", icon: "pi pi-image", command: () => handleInsertImageFromFile(editor) },
            { label: "Image from URL", icon: "pi pi-link", command: () => handleInsertImageFromUrl(editor) },
            { separator: true },
            { label: "YouTube video", icon: "pi pi-youtube", command: () => handleInsertYouTube(editor) },
            { label: "External content…", icon: "pi pi-external-link", command: () => setEmbedDialogRequest(request => ({ editor, id: (request?.id ?? 0) + 1 })) },
            { separator: true },
            { label: "Upload audio", icon: "pi pi-megaphone", command: () => handleInsertAudio(editor) },
            { separator: true },
            { label: "Insert graph", icon: "pi pi-sitemap", command: () => handleOpenNewGraph() },
            { separator: true },
            ...getInsertModel(editor, calloutInsertionAllowed),
            { separator: true },
            { label: 'Symbol...', icon: 'pi pi-face-smile', command: () => setCharacterDialogRequest(request => ({ editor, id: (request?.id ?? 0) + 1 })) },
        ].map(item => inFieldContainer && !['Insert field', 'Manage fields'].includes(item.label ?? '') ? { ...item, disabled: true } : item)} />

        {isTableActive && (
          <>
            <Button disabled={inFieldContainer} disabledReason="Click outside the form field to edit the surrounding lesson content." className="ce-btn" icon="pi pi-table" text onClick={(e) => tableMenu.current?.toggle(e)}
              aria-label="Table" title="Table" data-pr-tooltip="Table" />
            <Menu popup ref={tableMenu} model={getTableModel(editor, () => setTablePropsVisible(true))} />
          </>
        )}
      </div>

      <OverlayPanel
        ref={colorOp}
        showCloseIcon
        style={{ padding: 8 }}
        onShow={() => {
          const sel = editor.state.selection;
          savedSelectionRef.current = { from: sel.from, to: sel.to };
        }}
      >
        <div style={{ width: 220 }}>
          <ColorPicker inline format="hex" value={colorValue} onChange={(e) => setColorValue(e.value as string)} />
          <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
            <Button label="Apply" onClick={() => applyColor(colorValue)} />
            <Button label="Clear" className="p-button-secondary" onClick={clearColor} />
          </div>
        </div>
      </OverlayPanel>

      <OverlayPanel
        ref={bgColorOp}
        showCloseIcon
        style={{ padding: 8 }}
        onShow={() => {
          const sel = editor.state.selection;
          savedSelectionRef.current = { from: sel.from, to: sel.to };
        }}
      >
        <div style={{ width: 220 }}>
          <ColorPicker inline format="hex" value={bgColorValue} onChange={(e) => setBgColorValue(e.value as string)} />
          <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "flex-end" }}>
            <Button label="Apply" onClick={() => applyBgColor(bgColorValue)} />
            <Button label="Clear" className="p-button-secondary" onClick={clearBgColor} />
          </div>
        </div>
      </OverlayPanel>

      <PanelPropertiesDialog
        editor={editor}
        visible={panelDialogVisible}
        onHide={() => setPanelDialogVisible(false)}
      />
      <CardGroupDialog editor={editor} request={cardGroupRequest} />

      <LinkDialog editor={editor} api={api} request={linkDialogRequest?.editor === editor ? linkDialogRequest.id : 0} />
      <ExternalEmbedDialog editor={editor} request={embedDialogRequest?.editor === editor ? embedDialogRequest.id : 0} />
      <SpecialCharacterDialog editor={editor} request={characterDialogRequest?.editor === editor ? characterDialogRequest.id : 0} />
      <FieldContainerDialog editor={editor} request={fieldContainerRequest?.editor === editor ? fieldContainerRequest.id : 0} />

      <GeneralErrorDialog
        visible={error.length > 0}
        title={errorTitle}
        message={error}
        details={errorDetails}
        onClose={() => setError('')}
      />

      <TablePropertiesDialog
        editor={editor}
        visible={tablePropsVisible}
        onHide={() => setTablePropsVisible(false)}
      />

      <StyleInspectorModal 
        editor={editor} 
        isOpen={isInspectorOpen} 
        onClose={() => setIsInspectorOpen(false)} 
      />

      <GraphDialog 
        visible={graphDialogVisible} 
        onHide={() => {
            setGraphDialogVisible(false);
            setEditingGraphData(null);
        }} 
        onSave={handleSaveGraph}
        initialData={editingGraphData}
      />
    </div>
  );
};

export default RichToolbar;
